import { formatDateTime, formatDuration, formatTime, zoneLabel } from '../lib/format';
import { CalendarIcon, ClockIcon, PinIcon } from './Icons';
import { useI18n } from '../lib/i18n';

interface Props {
  startsAt: string;
  endsAt: string;
  /** The location's zone. */
  locationZone: string;
  /** The customer's zone. */
  customerZone: string;
  serviceName: string;
  durationMinutes?: number;
  providerName?: string | null;
  locationName?: string | null;
  address?: string | null;
  price?: string | null;
}

/** Where, when and with whom — shared by the confirmation and self-service pages. */
export function AppointmentSummary(p: Props) {
  const { t, locale } = useI18n();
  const zonesDiffer = p.locationZone !== p.customerZone;
  return (
    <dl className="summary">
      <div className="summary-row">
        <dt><CalendarIcon /> {t('summary.when')}</dt>
        <dd>
          <strong>{formatDateTime(p.customerZone, p.startsAt, locale)}</strong>
          <span className="muted"> – {formatTime(p.customerZone, p.endsAt, locale)}</span>
          <div className="small muted">{t('summary.your_time', { zone: zoneLabel(p.customerZone) })}</div>
          {zonesDiffer && (
            <div className="small muted">
              {t('summary.business_time', {
                when: formatDateTime(p.locationZone, p.startsAt, locale),
                zone: zoneLabel(p.locationZone),
              })}
            </div>
          )}
        </dd>
      </div>
      <div className="summary-row">
        <dt><ClockIcon /> {t('summary.service')}</dt>
        <dd>
          {p.serviceName}
          {p.durationMinutes ? <span className="muted"> · {formatDuration(p.durationMinutes, locale)}</span> : null}
          {p.price ? <span className="muted"> · {p.price}</span> : null}
          {p.providerName ? <div className="small muted">{t('summary.with', { name: p.providerName })}</div> : null}
        </dd>
      </div>
      {(p.locationName || p.address) && (
        <div className="summary-row">
          <dt><PinIcon /> {t('summary.where')}</dt>
          <dd>
            {p.locationName}
            {p.address && (
              <div className="small">
                <a href={`https://maps.google.com/?q=${encodeURIComponent(p.address)}`} rel="noopener" target="_blank">{p.address}</a>
              </div>
            )}
          </dd>
        </div>
      )}
    </dl>
  );
}
